import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XMarkIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/20/solid';

import { Modal, ModalContent, Button, Tooltip } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { useOrganizationsService, ReferralRequest } from '@restworld/data-services';

import { useReferralCredits } from './use-referral-credits';

interface ReferralRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  requests: ReferralRequest[];
}

// Modal for employers to view, accept, or decline incoming referral requests from prospects
export function ReferralRequestsModal({ isOpen, onClose, requests }: ReferralRequestsModalProps) {
  const { t } = useTranslation('referral-page');
  const organizationsService = useOrganizationsService();
  const queryClient = useQueryClient();
  const [acceptedRequest, setAcceptedRequest] = useState<ReferralRequest | null>(null);
  const { inviter, invitee } = useReferralCredits();

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'accepted' | 'rejected' }) =>
      organizationsService.updateReferralRequest(id, { status }),
    onSuccess: (_, { id, status }) => {
      if (status === 'accepted') {
        const request = requests.find((r) => r.id === id);
        if (request) setAcceptedRequest(request);
      }
      queryClient.invalidateQueries({ queryKey: ['referral-requests'] });
    },
  });

  const handleClose = () => {
    setAcceptedRequest(null);
    onClose();
  };

  // Show the "Request accepted" confirmation
  if (acceptedRequest) {
    return (
      <Modal open={isOpen} onOpenChange={(v) => !v && handleClose()}>
        <ModalContent
          className="w-full max-w-[640px]"
          footer={
            <AcceptedFooter>
              <Button variant="primary" fullwidth onClick={handleClose}>
                {t('request_accepted_cta')}
              </Button>
            </AcceptedFooter>
          }
        >
          <AcceptedContentWrapper>
            <div className="flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-500" />
            </div>
            <AcceptedTitle>{t('request_accepted_title')}</AcceptedTitle>
            <AcceptedDescription>
              {t('request_accepted_description', {
                prospect_venue: acceptedRequest.prospect_restaurant_name,
                inviter,
                invitee,
              })}
            </AcceptedDescription>
          </AcceptedContentWrapper>
        </ModalContent>
      </Modal>
    );
  }

  const pendingRequests = requests.filter((r) => r.status === 'pending');
  const acceptedRequests = requests.filter((r) => r.status === 'accepted');
  const invalidatedRequests = requests.filter((r) => r.status === 'invalidated');
  const hasRequests = pendingRequests.length > 0 || acceptedRequests.length > 0 || invalidatedRequests.length > 0;

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <ModalContent className="w-full max-w-[640px]" noPadding>
        <ModalBody hasRequests={hasRequests}>
          {!hasRequests ? (
            <NoRequestsText>
              {t(
                'no_requests',
                'Quando altri ristoratori ti chiederanno un invito, le loro richieste compariranno qui. Accettale per guadagnare giorni di ricerca gratuiti'
              )}
            </NoRequestsText>
          ) : (
            <>
              {pendingRequests.length > 0 && (
                <>
                  <CardsContainer>
                    {pendingRequests.map((request) => (
                      <RequestCard
                        key={request.id}
                        request={request}
                        onAccept={() => updateMutation.mutate({ id: request.id, status: 'accepted' })}
                        onDecline={() => updateMutation.mutate({ id: request.id, status: 'rejected' })}
                        isAccepting={
                          updateMutation.isPending &&
                          updateMutation.variables?.id === request.id &&
                          updateMutation.variables?.status === 'accepted'
                        }
                        isDeclining={
                          updateMutation.isPending &&
                          updateMutation.variables?.id === request.id &&
                          updateMutation.variables?.status === 'rejected'
                        }
                        t={t}
                      />
                    ))}
                  </CardsContainer>
                </>
              )}

              {pendingRequests.length > 0 && (acceptedRequests.length > 0 || invalidatedRequests.length > 0) && (
                <SectionDivider />
              )}

              {(acceptedRequests.length > 0 || invalidatedRequests.length > 0) && (
                <CardsContainer>
                  {acceptedRequests.map((request) => (
                    <AcceptedCard key={request.id} request={request} t={t} />
                  ))}
                  {invalidatedRequests.map((request) => (
                    <InvalidatedCard key={request.id} request={request} t={t} />
                  ))}
                </CardsContainer>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

interface RequestCardProps {
  request: ReferralRequest;
  onAccept: () => void;
  onDecline: () => void;
  isAccepting: boolean;
  isDeclining: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function RequestCard({ request, onAccept, onDecline, isAccepting, isDeclining, t }: RequestCardProps) {
  const formattedDate = new Date(request.inserted_at).toLocaleDateString();

  return (
    <CardWrapper>
      <CardHeader>
        <VenueName>{request.prospect_restaurant_name}</VenueName>
        <MetaRow>
          {request.prospect_restaurant_address && (
            <MetaItem>
              <MapPinIcon className="w-4 h-4 flex-shrink-0" />
              <span>{request.prospect_restaurant_address}</span>
            </MetaItem>
          )}
          <MetaItem>
            <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
            <span>{t('sent_on', { date: formattedDate })}</span>
          </MetaItem>
        </MetaRow>
      </CardHeader>

      <MessageSection>
        <MessageQuote>
          {t('request_message', {
            prospect_venue: request.prospect_restaurant_name,
          })}
        </MessageQuote>
      </MessageSection>

      <Divider />

      <ActionsRow>
        <Button
          variant="tertiary"
          StartIcon={XMarkIcon}
          onClick={onDecline}
          loading={isDeclining}
          disabled={isAccepting}
          fullwidth
        >
          {t('decline')}
        </Button>
        <Button
          variant="primary"
          StartIcon={CheckIcon}
          onClick={onAccept}
          loading={isAccepting}
          disabled={isDeclining}
          fullwidth
        >
          {t('accept')}
        </Button>
      </ActionsRow>
    </CardWrapper>
  );
}

interface AcceptedCardProps {
  request: ReferralRequest;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function AcceptedCard({ request, t }: AcceptedCardProps) {
  const formattedDate = new Date(request.inserted_at).toLocaleDateString();

  return (
    <CardWrapper>
      <CardHeader>
        <VenueName>{request.prospect_restaurant_name}</VenueName>
        <MetaRow>
          {request.prospect_restaurant_address && (
            <MetaItem>
              <MapPinIcon className="w-4 h-4 flex-shrink-0" />
              <span>{request.prospect_restaurant_address}</span>
            </MetaItem>
          )}
          <MetaItem>
            <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
            <span>{t('sent_on', { date: formattedDate })}</span>
          </MetaItem>
        </MetaRow>
      </CardHeader>

      <MessageSection>
        <MessageQuote>
          {t('request_message', {
            prospect_venue: request.prospect_restaurant_name,
          })}
        </MessageQuote>
      </MessageSection>

      <Divider />

      <StatusRow>
        <CheckCircleIcon className="w-5 h-5 text-green-500" />
        <StatusText>{t('request_accepted')}</StatusText>
      </StatusRow>
    </CardWrapper>
  );
}

function InvalidatedCard({ request, t }: AcceptedCardProps) {
  const formattedDate = new Date(request.inserted_at).toLocaleDateString();

  return (
    <InvalidatedCardWrapper>
      <CardHeader>
        <InvalidatedVenueName>{request.prospect_restaurant_name}</InvalidatedVenueName>
        <MetaRow>
          {request.prospect_restaurant_address && (
            <MetaItem>
              <MapPinIcon className="w-4 h-4 flex-shrink-0" />
              <span>{request.prospect_restaurant_address}</span>
            </MetaItem>
          )}
          <MetaItem>
            <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
            <span>{t('sent_on', { date: formattedDate })}</span>
          </MetaItem>
        </MetaRow>
      </CardHeader>

      <MessageSection>
        <InvalidatedMessageQuote>
          {t('request_message', {
            prospect_venue: request.prospect_restaurant_name,
          })}
        </InvalidatedMessageQuote>
      </MessageSection>

      <Divider />

      <StatusRow>
        <Tooltip content={t('request_invalidated_reason')} placement="top">
          <InformationCircleIcon className="w-5 h-5 text-neutral-200 cursor-pointer" />
        </Tooltip>
        <InvalidatedStatusText>{t('request_invalidated')}</InvalidatedStatusText>
      </StatusRow>
    </InvalidatedCardWrapper>
  );
}

// Layout Components
// This is the scroll container. `justify-center` only for the empty state: with a list,
// centring splits the overflow above and below the viewport, and the part above is
// unreachable because scrollTop cannot go negative — the pending cards render first, get
// pushed off the top, and look like they are missing. Anything scrollable starts at the top.
const ModalBody = ({
  hasRequests,
  className,
  ...props
}: React.HTMLAttributes<HTMLElement> & { hasRequests: boolean }) => (
  <div
    className={cn(
      'flex flex-1 flex-col items-center gap-12 max-h-[70vh] overflow-y-auto px-10 py-6',
      hasRequests ? 'justify-start' : 'justify-center',
      className
    )}
    {...props}
  />
);

const NoRequestsText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-base text-neutral-90 text-center', className)} {...props} />
);

const CardsContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);

const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-2xl overflow-hidden bg-neutral-10 border border-neutral-90', className)} {...props} />
);

const CardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2 p-4', className)} {...props} />
);

const VenueName = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-base font-bold text-neutral-700', className)} {...props} />
);

const MetaRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-row gap-2', className)} {...props} />
);

const MetaItem = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-1 text-neutral-200 text-sm font-medium', className)} {...props} />
);

const MessageSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 px-4 pb-4', className)} {...props} />
);

const MessageQuote = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-lg bg-neutral-30 p-4 text-base text-neutral-700', className)} {...props} />
);

const Divider = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('h-px bg-neutral-40', className)} {...props} />
);

const ActionsRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-stretch gap-4 p-4', className)} {...props} />
);

const AcceptedContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center gap-4 text-center px-10 pt-10 pb-20', className)} {...props} />
);

const AcceptedTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('font-semibold text-xl text-center text-neutral-700', className)} {...props} />
);

const AcceptedDescription = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-center whitespace-pre-line text-base leading-tight text-neutral-700', className)} {...props} />
);

const AcceptedFooter = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full px-4 pb-4 pt-3 border-t border-neutral-40', className)} {...props} />
);

const SectionDivider = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('h-px bg-neutral-40 my-2', className)} {...props} />
);

const StatusRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-center gap-2 p-4', className)} {...props} />
);

const StatusText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('font-semibold text-sm text-green-500', className)} {...props} />
);

const InvalidatedCardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-2xl overflow-hidden bg-neutral-20 border border-neutral-90', className)} {...props} />
);

const InvalidatedVenueName = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-base font-bold text-neutral-200', className)} {...props} />
);

const InvalidatedMessageQuote = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-lg bg-neutral-30 p-4 text-base text-neutral-200', className)} {...props} />
);

const InvalidatedStatusText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('font-semibold text-base text-neutral-700', className)} {...props} />
);
